import { StatusActiveIcon } from '@tableau/icons/StatusActiveIcon';
import { StatusErrorIcon } from '@tableau/icons/StatusErrorIcon';
import { StatusInProgressIcon } from '@tableau/icons/StatusInProgressIcon';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { BodyCell, DataGrid, GridSelection, HeaderColumn } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { PlainTextCellWithTransform } from '~src/spacesPlatform/components/ListView/dataGridCells/PlainTextAndNumberCells';
import { StyledCellFormatter, StyledHeaderFormatter } from '~src/spacesPlatform/components/ListView/StyledCellWrapper';
import { classes } from '~src/style';

import { IJobHistoryItemVM, JobHistoryStatusBadgeColor } from './IJobHistory';
import { TaskDetailsSpaceStyles } from './TaskDetailsSpaceStyles';

const STATUS_ICON_SIZE = 16;

// How close (in rows) the rendered viewport must get to the end of the loaded jobs before we
// request the next page. Mirrors ListViewGridContentGenerator.shouldRequestNextPage's endOffset.
const LOAD_MORE_ROW_OFFSET = 10;

// Fixed row height sized for 3 lines: the widget uses one height for all rows, so a long Status
// Message wraps up to 3 lines while single-value cells center vertically within it.
const HISTORY_ROW_OUTER_HEIGHT = 64;
const STATUS_MESSAGE_MAX_LINES = 3;
const JOB_LUID_PREFERRED_WIDTH = 340;
const STATUS_MESSAGE_PREFERRED_WIDTH = 420;
// Wide enough for a "Pool: {pool}/Client: {client}" line without immediate wrapping in the common
// single-pool case; multi-pool rows wrap/clamp like Status Message and overflow to the tooltip.
const BRIDGE_PREFERRED_WIDTH = 320;

// Frames to wait for a deep-link target to render (it can be paged in async) before giving up on
// top-aligning it. ~6 frames is imperceptible and self-terminating so it can't loop.
const MAX_TOP_ALIGN_FRAMES = 6;

// Walks up to the nearest vertically-scrollable ancestor -- the DataGrid body's own scroll
// container. We top-align the deep-link target by scrolling THIS element only, rather than native
// scrollIntoView (which has no sticky-header compensation and also scrolls outer page containers).
const findScrollParent = (el: HTMLElement): HTMLElement | null => {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

/**
 * Pure scroll math for the deep-link top-align, factored out so it's unit-testable without a real
 * (JSDOM-less) layout. Returns the scrollTop that parks the target row's top just below the grid's
 * sticky header, clamped to >= 0. `rowTop`/`scrollerTop` are viewport coordinates
 * (getBoundingClientRect().top); `scrollerScrollTop` is the scroller's current scrollTop; and
 * `headerHeight` is the sticky header's height (subtracted so the row clears it -- the bug this fixes
 * was using native scrollIntoView, which parked the row *under* the sticky header).
 */
export const computeTopAlignScrollTop = (params: {
  rowTop: number;
  scrollerTop: number;
  scrollerScrollTop: number;
  headerHeight: number;
}): number => {
  const rowTopWithinScroller = params.rowTop - params.scrollerTop + params.scrollerScrollTop;
  return Math.max(0, rowTopWithinScroller - params.headerHeight);
};

// Maps the semantic status color to a design-system status glyph (each carries its own color).
// Cancelled/neutral (gray) has no colored glyph, so the label renders on its own.
const renderStatusIcon = (color: JobHistoryStatusBadgeColor): JSX.Element | null => {
  switch (color) {
    case 'green':
      return <StatusActiveIcon size={STATUS_ICON_SIZE} alt="" />;
    case 'red':
      return <StatusErrorIcon size={STATUS_ICON_SIZE} alt="" />;
    case 'yellow':
      return <StatusInProgressIcon size={STATUS_ICON_SIZE} alt="" />;
    case 'gray':
      return null;
  }
};

// Column ids (also used as `dataKey`, so each maps to a field on IJobHistoryItemVM, and as the
// cell `data-tb-test-id` prefix — the widget renders `data-tb-test-id="{name}-cell"`).
export const JobHistoryColumn = {
  JobId: 'jobId',
  Luid: 'luid',
  Status: 'status',
  StatusMessage: 'statusMessage',
  ScheduledAt: 'scheduledAt',
  StartedAt: 'startedAt',
  CompletedAt: 'completedAt',
  QueueTime: 'queueTime',
  RunTime: 'runTime',
  // Single merged Bridge column (W-23518891): 'Pool: X/Client: Y' per pool. Replaces the former
  // BridgePool / BridgeClient columns. 'bridge' is a stable id (not an IJobHistoryItemVM field --
  // BridgeCell reads bridgeLines directly).
  Bridge: 'bridge',
} as const;

export const JobHistoryTableTestIds = {
  EmptyState: 'task-details-history-empty-state',
  Grid: 'task-details-history-grid',
  LoadError: 'task-details-history-load-error',
  ScrollAnnouncement: 'task-details-history-scroll-announcement',
  PaginationAnnouncement: 'task-details-history-pagination-announcement',
};

export interface JobHistoryTableProps {
  readonly jobs: ReadonlyArray<IJobHistoryItemVM>;
  readonly isLoadingMore: boolean;
  readonly hasMore: boolean;
  readonly hasLoadError: boolean;
  readonly onLoadMore: () => void;
  readonly onRetryLoadMore: () => void;
  /** Id of the job to autoscroll to and highlight (S08-b), parsed from the route's `jobId` query param. */
  readonly jobId?: string | null;
  /** Requests another page of history when `jobId` hasn't been found in the currently loaded jobs. */
  readonly onRequestNextPageForJob?: () => void;
}

// Renders a dash for absent/empty/whitespace-only values (a bare `?? '-'` would leave '' as a blank
// cell); otherwise returns the value unchanged.
const valueOrDash = (value: string | null): string => (value != null && value.trim() !== '' ? value : '-');

// Plain-text cells reuse the shared StyledCellFormatter wrapper so padding/clipping matches every
// other list surface; valueOrDash supplies the dash for empty values.
const textCellFor = (dataKey: string) => PlainTextCellWithTransform(dataKey, valueOrDash);

// The widget renders header text with no padding, so pad it with the same MediumSpace as the body
// cells' StyledCellFormatter to keep headers aligned over their columns.
const HistoryHeaderCell = StyledHeaderFormatter(
  function HistoryHeader(props: HeaderColumn.HeaderFormatterProps) {
    return <span>{props.col.headerText}</span>;
  },
  {
    paddingLeft: CommonStyles.Sizes.MediumSpace,
    paddingRight: CommonStyles.Sizes.MediumSpace,
    ...CommonStyles.Layout.TextEllipsis,
  }
);

// Status body cell: colored design-system glyph + label, wrapped in the same shared cell wrapper so
// it gets identical padding/alignment to the other cells.
const StatusCell = StyledCellFormatter((props: BodyCell.Props) => {
  const job = props.row.data as IJobHistoryItemVM;
  return (
    <span
      className={TaskDetailsSpaceStyles.StatusIndicator}
      aria-label={`${Messages.taskDetailsHistoryHeaderStatus()}: ${job.status}`}
    >
      {renderStatusIcon(job.statusBadgeColor)}
      {job.status}
    </span>
  );
});

// Inner-span style shared by the multi-line cells (Status Message and Bridge): a 3-line
// `-webkit-box` clamp that overrides StyledCellFormatter's single-line clip, with the full text
// surfaced via the cell tooltip. `whiteSpace` is the only knob: 'normal' lets a long single value
// wrap naturally; 'pre-line' additionally honors explicit '\n' breaks (one per Bridge pool) while
// still wrapping long pool/client names. Kept as one factory so the two cells can't drift.
const clampInnerStyle = (whiteSpace: 'normal' | 'pre-line'): React.CSSProperties => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: STATUS_MESSAGE_MAX_LINES,
  width: '100%',
  maxWidth: '100%',
  lineHeight: '20px',
  whiteSpace,
  overflowWrap: 'anywhere',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const CLAMP_OUTER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'normal',
  height: '100%',
} as React.CSSProperties;

// Status Message cell wraps and clamps to 3 lines (overriding StyledCellFormatter's single-line
// clip); the full text stays available via the cell tooltip.
const StatusMessageCell = StyledCellFormatter(
  (props: BodyCell.Props) => {
    const job = props.row.data as IJobHistoryItemVM;
    return (
      <span
        style={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <span style={clampInnerStyle('normal')}>{valueOrDash(job.statusMessage)}</span>
      </span>
    );
  },
  CLAMP_OUTER_STYLE
);

// Shows the full message on hover whenever there is content (the multi-line clamp defeats the
// widget's single-line clip detection), preferring the fuller tooltip text when present.
const statusMessageTooltip = (cellProps: BodyCell.Props) => {
  const job = cellProps.row.data as IJobHistoryItemVM;
  const full = job.statusMessageTooltip ?? job.statusMessage;
  return full != null && full.trim() !== ''
    ? { getContent: () => <span>{full}</span>, onlyShowWhenClipped: false }
    : {};
};

// Bridge cell: the single "Bridge" column (W-23518891). Renders one localized
// "Pool: X/Client: Y" line per pool (bridgeLines, joined by '\n' and shown via 'pre-line'), reusing
// the Status Message 3-line clamp so a multi-pool run wraps identically; the full list is always
// available in the tooltip. Empty -> dash.
const BridgeCell = StyledCellFormatter((props: BodyCell.Props) => {
  const job = props.row.data as IJobHistoryItemVM;
  const lines = job.bridgeLines;
  const text = lines != null && lines.length > 0 ? lines.join('\n') : null;
  return (
    <span
      style={{
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <span style={clampInnerStyle('pre-line')}>{valueOrDash(text)}</span>
    </span>
  );
}, CLAMP_OUTER_STYLE);

// Full multi-pool list on hover (the clamp hides overflow beyond 3 lines); rendered 'pre-line' so
// each pool stays on its own line.
const bridgeTooltip = (cellProps: BodyCell.Props) => {
  const job = cellProps.row.data as IJobHistoryItemVM;
  const lines = job.bridgeLines;
  return lines != null && lines.length > 0
    ? { getContent: () => <span style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</span>, onlyShowWhenClipped: false }
    : {};
};

const buildColumns = (hasBridgeJobs: boolean): DataGrid.Column[] => {
  const cols: DataGrid.Column[] = [
    { name: JobHistoryColumn.JobId, dataKey: JobHistoryColumn.JobId, headerText: Messages.taskDetailsHistoryHeaderJobId(), cellFormatter: textCellFor(JobHistoryColumn.JobId) },
    {
      name: JobHistoryColumn.Luid,
      dataKey: JobHistoryColumn.Luid,
      headerText: Messages.taskDetailsHistoryHeaderJobLuid(),
      cellFormatter: textCellFor(JobHistoryColumn.Luid),
      preferredWidth: JOB_LUID_PREFERRED_WIDTH,
    },
    { name: JobHistoryColumn.Status, dataKey: JobHistoryColumn.Status, headerText: Messages.taskDetailsHistoryHeaderStatus(), cellFormatter: StatusCell },
    {
      name: JobHistoryColumn.StatusMessage,
      dataKey: JobHistoryColumn.StatusMessage,
      headerText: Messages.taskDetailsHistoryHeaderStatusMessage(),
      cellFormatter: StatusMessageCell,
      cellTooltip: statusMessageTooltip,
      preferredWidth: STATUS_MESSAGE_PREFERRED_WIDTH,
      growToFit: true,
    },
    { name: JobHistoryColumn.ScheduledAt, dataKey: JobHistoryColumn.ScheduledAt, headerText: Messages.taskDetailsHistoryHeaderScheduledAt(), cellFormatter: textCellFor(JobHistoryColumn.ScheduledAt) },
    { name: JobHistoryColumn.StartedAt, dataKey: JobHistoryColumn.StartedAt, headerText: Messages.taskDetailsHistoryHeaderStartedAt(), cellFormatter: textCellFor(JobHistoryColumn.StartedAt) },
    { name: JobHistoryColumn.CompletedAt, dataKey: JobHistoryColumn.CompletedAt, headerText: Messages.taskDetailsHistoryHeaderCompletedAt(), cellFormatter: textCellFor(JobHistoryColumn.CompletedAt) },
    { name: JobHistoryColumn.QueueTime, dataKey: JobHistoryColumn.QueueTime, headerText: Messages.taskDetailsHistoryHeaderQueueTime(), cellFormatter: textCellFor(JobHistoryColumn.QueueTime) },
    { name: JobHistoryColumn.RunTime, dataKey: JobHistoryColumn.RunTime, headerText: Messages.taskDetailsHistoryHeaderRunTime(), cellFormatter: textCellFor(JobHistoryColumn.RunTime) },
  ];
  if (hasBridgeJobs) {
    cols.push({
      name: JobHistoryColumn.Bridge,
      dataKey: JobHistoryColumn.Bridge,
      headerText: Messages.taskDetailsHistoryHeaderBridge(),
      cellFormatter: BridgeCell,
      cellTooltip: bridgeTooltip,
      preferredWidth: BRIDGE_PREFERRED_WIDTH,
    });
  }
  // Every column shares the same MediumSpace-padded header formatter so the headers line up with the
  // MediumSpace-padded body cells (see HistoryHeaderCell). Applied here so new columns can't forget it.
  return cols.map(col => ({ ...col, headerFormatter: HistoryHeaderCell }));
};

/**
 * Job Run History table. Renders through the shared `DataGrid` widget with incremental loading,
 * autoscroll-to-job, a transient highlight, and polite live-region announcements.
 */
export const JobHistoryTable: React.FunctionComponent<JobHistoryTableProps> = props => {
  const gridRef = React.useRef<DataGrid>(null);
  const gridBoxRef = React.useRef<HTMLDivElement>(null);
  const loadMoreQueuedRef = React.useRef(false);
  const previousJobCountRef = React.useRef(props.jobs.length);
  const scrolledForJobIdRef = React.useRef<string | null>(null);
  const [highlightedJobId, setHighlightedJobId] = React.useState<string | null>(null);
  const [scrollAnnouncement, setScrollAnnouncement] = React.useState<string>('');
  const [paginationAnnouncement, setPaginationAnnouncement] = React.useState<string>('');

  // Highlight is read via a ref inside a STABLE selectionModel (the widget requires a persistent
  // model), repainted with forceSelectionRefresh(); cell.rowKey is the job.jobId we set as Row.key.
  const highlightedJobIdRef = React.useRef<string | null>(null);
  const selectionModelRef = React.useRef<GridSelection>({
    isSelected: cell => highlightedJobIdRef.current != null && cell.rowKey === highlightedJobIdRef.current,
  });

  // Autoscroll to the job that navigated here. Re-checks whenever `jobs` changes so a target that
  // arrives on a later page still gets found (timer-free retry).
  React.useEffect(() => {
    if (!props.jobId) {
      // Re-arm so a jobId -> null -> same-jobId round trip (clicking the same job again after it
      // was cleared) scrolls again rather than being swallowed by the scroll-once guard below.
      scrolledForJobIdRef.current = null;
      return;
    }
    if (scrolledForJobIdRef.current === props.jobId) {
      return;
    }
    const targetJobId = props.jobId;
    const found = props.jobs.some(job => job.jobId === targetJobId);
    if (found) {
      scrolledForJobIdRef.current = targetJobId;
      highlightedJobIdRef.current = targetJobId;
      setHighlightedJobId(targetJobId);
      setScrollAnnouncement(Messages.taskDetailsScrolledToJob({ JOB_ID: targetJobId }));
      // Defer a frame so the grid has committed the new rows, then focusCellByKey pages the row in,
      // scrolls it into view, and moves focus onto it.
      requestAnimationFrame(() => {
        gridRef.current?.focusCellByKey(targetJobId);
        gridRef.current?.forceSelectionRefresh();
      });
      return;
    }
    props.onRequestNextPageForJob?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.jobId, props.jobs]);

  // focusCellByKey only guarantees the target is visible (widget scrolls block:'nearest'); the deep
  // link should put it at the top of the pane. Once the highlighted row is committed and paged into
  // the DOM (a later-page target arrives async), top-align it. Bounded retry so it self-terminates.
  React.useEffect(() => {
    if (!highlightedJobId) {
      return undefined;
    }
    let framesLeft = MAX_TOP_ALIGN_FRAMES;
    let rafId = 0;
    const alignToTop = () => {
      const row = gridBoxRef.current?.querySelector<HTMLElement>('[role="row"][aria-selected="true"]');
      if (row) {
        // Header-aware top-align. Native scrollIntoView({block:'start'}) has NO sticky-header
        // compensation, so it parks the row *under* the DataGrid's sticky header (the row looked
        // mis-aligned until a keypress re-ran the widget's header-aware focus scroll). Instead
        // scroll only the grid's own container so the row's top lands just below the sticky header.
        const scroller = findScrollParent(row);
        if (scroller) {
          const header = gridBoxRef.current?.querySelector<HTMLElement>('.tb-react-dg-hcell');
          scroller.scrollTop = computeTopAlignScrollTop({
            rowTop: row.getBoundingClientRect().top,
            scrollerTop: scroller.getBoundingClientRect().top,
            scrollerScrollTop: scroller.scrollTop,
            headerHeight: header?.offsetHeight ?? 0,
          });
        } else {
          // Fallback if the scroll container can't be found: at least keep the row visible without
          // yanking outer page containers around.
          row.scrollIntoView({ block: 'nearest' });
        }
        return;
      }
      if (framesLeft-- > 0) {
        rafId = requestAnimationFrame(alignToTop);
      }
    };
    rafId = requestAnimationFrame(alignToTop);
    return () => cancelAnimationFrame(rafId);
  }, [highlightedJobId]);

  // Cosmetic fade: clears the highlight ~2s after it's applied.
  React.useEffect(() => {
    if (!highlightedJobId) {
      return;
    }
    const timeoutId = setTimeout(() => {
      highlightedJobIdRef.current = null;
      setHighlightedJobId(null);
      gridRef.current?.forceSelectionRefresh();
    }, 2000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [highlightedJobId]);

  React.useEffect(() => {
    if (!props.isLoadingMore) {
      loadMoreQueuedRef.current = false;
    }
  }, [props.isLoadingMore]);

  // After pagination loads more rows, announce the count to screen readers via a rendered polite
  // live region (below). Rendering it in-tree — rather than hand-appending a node to document.body —
  // means React tears it down on unmount, so no orphaned nodes leak across the app or in tests.
  React.useEffect(() => {
    if (props.jobs.length > previousJobCountRef.current && !props.isLoadingMore) {
      const newJobsCount = props.jobs.length - previousJobCountRef.current;
      setPaginationAnnouncement(Messages.taskDetailsHistoryLoadedMore({ COUNT: String(newJobsCount) }));
    }
    previousJobCountRef.current = props.jobs.length;
  }, [props.jobs.length, props.isLoadingMore]);

  // Volatile load-more inputs read from inside the (memoized) rowStore's getItems. Kept in a ref so
  // the rowStore closure never goes stale even though rowStore identity only changes with `jobs`.
  const loadMoreInputsRef = React.useRef({
    hasMore: props.hasMore,
    hasLoadError: props.hasLoadError,
    isLoadingMore: props.isLoadingMore,
    onLoadMore: props.onLoadMore,
  });
  loadMoreInputsRef.current = {
    hasMore: props.hasMore,
    hasLoadError: props.hasLoadError,
    isLoadingMore: props.isLoadingMore,
    onLoadMore: props.onLoadMore,
  };

  // Rebuilt only when `jobs` changes. `data: { ...job }` (a fresh literal, not `data: job`) satisfies
  // DataGrid.Row.data's Dictionary<any> index signature.
  const rows = React.useMemo<DataGrid.Row[]>(
    () => props.jobs.map(job => ({ key: job.jobId, data: { ...job } })),
    [props.jobs]
  );

  // Memoized on `rows` so a new page yields a new rowStore identity (making the widget refetch) while
  // unrelated re-renders reuse it; getItems requests more as the viewport nears the end.
  const rowStore = React.useMemo<DataGrid.RowStore>(
    () => ({
      getItems: (_start, end, rowHandler) => {
        rowHandler(rows);
        const { hasMore, hasLoadError, isLoadingMore, onLoadMore } = loadMoreInputsRef.current;
        const nearEnd = end + LOAD_MORE_ROW_OFFSET >= rows.length;
        if (nearEnd && hasMore && !hasLoadError && !isLoadingMore && !loadMoreQueuedRef.current) {
          loadMoreQueuedRef.current = true;
          onLoadMore();
        }
      },
    }),
    [rows]
  );

  if (props.jobs.length === 0 && !props.hasLoadError) {
    return (
      <div className={TaskDetailsSpaceStyles.HistoryPlaceholder} data-tb-test-id={JobHistoryTableTestIds.EmptyState}>
        {Messages.taskDetailsHistoryNoJobs()}
      </div>
    );
  }

  // Show the Bridge column when any loaded row has Bridge lines (adapter derives these from
  // poolAgentMap, or the legacy scalars as a fallback).
  const hasBridgeJobs = props.jobs.some(job => job.bridgeLines != null && job.bridgeLines.length > 0);
  const cols = buildColumns(hasBridgeJobs);

  return (
    <div
      className={TaskDetailsSpaceStyles.HistoryGridRegion}
      role="region"
      aria-label={Messages.taskDetailsJobHistoryHeading()}
    >
      <div className={TaskDetailsSpaceStyles.HistoryGridBox} ref={gridBoxRef}>
        <DataGrid
          ref={gridRef}
          className={classes(ExplorerStyles.DataGridBodyTopBorder, TaskDetailsSpaceStyles.HistoryGrid)}
          data-tb-test-id={JobHistoryTableTestIds.Grid}
          aria-label={Messages.taskDetailsHistoryTableAriaLabel()}
          cols={cols}
          rowStore={rowStore}
          config={{
            rowOuterHeight: HISTORY_ROW_OUTER_HEIGHT,
            colsPerChunk: cols.length,
            rememberLastScrollPos: true,
          }}
          selectionModel={selectionModelRef.current}
          showVerticalGridLines={false}
          showHorizontalGridLines={true}
          showRowBanding={false}
        />
      </div>
      {props.hasLoadError && (
        <div className={TaskDetailsSpaceStyles.HistoryLoadState} data-tb-test-id={JobHistoryTableTestIds.LoadError}>
          {Messages.taskDetailsHistoryLoadError()}
          <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={props.onRetryLoadMore}>
            {Messages.taskDetailsHistoryRetryLoad()}
          </ButtonWidget>
        </div>
      )}
      {/* Visually-hidden polite live regions: announce autoscroll-to-target and pagination loads to
          screen readers. Rendered in-tree (not appended to document.body) so React owns their lifecycle. */}
      <div
        className={TaskDetailsSpaceStyles.VisuallyHidden}
        role="status"
        aria-live="polite"
        data-tb-test-id={JobHistoryTableTestIds.ScrollAnnouncement}
      >
        {scrollAnnouncement}
      </div>
      <div
        className={TaskDetailsSpaceStyles.VisuallyHidden}
        role="status"
        aria-live="polite"
        data-tb-test-id={JobHistoryTableTestIds.PaginationAnnouncement}
      >
        {paginationAnnouncement}
      </div>
    </div>
  );
};
